import * as _ from 'lodash';
import moment from 'moment';
import type { VoidFunctionComponent } from 'react';

import { BillingTermType } from '../../../../../shared/model-typings';
import { useDialogContext } from '../../../../contexts/DialogContext';
import { useAsyncStatus } from '../../../../hooks';
import * as api from '../../../../redux/api';
import { Badge, BadgeColors, BUTTON_SIZE, BUTTON_TYPE, icons, LoaderButton } from '../../../generic';
import List from '../../../generic/List';
import type { BillingTermWithUsageFields } from '../../../../../shared/model-typings/billingterm';
import type { FailedFetchResponse } from '../../../../helpers/errors';

import { BILLING_TERM_URL } from '.';

type Props = {
  billingTerm: BillingTermWithUsageFields;
  onRemove: () => void;
};

const BillingTermRow: VoidFunctionComponent<Props> = ({
  billingTerm,
  onRemove,
}) => {
  const { status, onError, onPending, onSuccess } = useAsyncStatus();
  const { onConfirm } = useDialogContext();

  const {
    id,
    quantity,
    usage,
    createdBy,
    isActive,
    type,
  } = billingTerm;

  const termStart = moment(billingTerm.termStart).utc().format('MM/DD/YYYY');
  const termEnd = moment(billingTerm.termEnd).utc().format('MM/DD/YYYY');

  const removeTerm = async () => {
    onPending();
    try {
      await api.remove(`${BILLING_TERM_URL}/${id}`);
      onRemove();
      onSuccess();
    } catch (err) {
      onError(err as FailedFetchResponse);
    }
  };

  const onRemoveClick = async () => {
    onConfirm({
      title: 'Remove Billing Term',
      body: `Are you sure you want to remove this billing term of ${quantity} revisions, from ${termStart} - ${termEnd}?`,
      onConfirm: removeTerm,
    });
  };

  return (
    <List.RowContainer>
      <List.Row key={id}>
        <List.RowTitle>
          <div>
            {termStart} - {termEnd}
            {isActive && <Badge color={BadgeColors.Green}>Active</Badge>}
          </div>
          <LoaderButton
            type={BUTTON_TYPE.ANCHOR}
            size={BUTTON_SIZE.SMALL}
            icon={icons.times}
            isLoading={status.isLoading}
            isDisabled={status.isLoading}
            onClick={onRemoveClick}
            />
        </List.RowTitle>
        <List.RowDetails>
          <List.RowDetailsItem>
            {_.capitalize(type)} Usage
            <div className="margin-left-xs">
              <Badge color={usage <= quantity ? BadgeColors.LightGray : BadgeColors.DarkRed}>
                {usage} / {quantity}
              </Badge>
            </div>
          </List.RowDetailsItem>
          <List.RowDetailsItem>
            Created By:
            <div className="margin-left-xs">
              <Badge color={BadgeColors.LightGray}>
                {createdBy}
              </Badge>
            </div>
          </List.RowDetailsItem>
          <List.RowDetailsItem>
            Type:
            <div className="margin-left-xs">
              <Badge color={type === BillingTermType.Revision ? BadgeColors.LightGreen : BadgeColors.LightBlue}>
                {type.toUpperCase()}
              </Badge>
            </div>
          </List.RowDetailsItem>
        </List.RowDetails>
      </List.Row>
    </List.RowContainer>
  );
};

export default BillingTermRow;
